import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ImageIcon, Loader2, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const LogoManager = ({ tenantId, hasLogo = false, onChange, testId = "logo-manager" }) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const loadLogo = useCallback(async () => {
    if (!tenantId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/tenants/${tenantId}/logo`, { responseType: "blob" });
      setPreview(URL.createObjectURL(data));
    } catch { setPreview(null); }
    finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { loadLogo(); }, [loadLogo]);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Logo must be smaller than 5 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/tenants/${tenantId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo updated");
      await loadLogo();
      onChange?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/tenants/${tenantId}/logo`);
      setPreview(null);
      toast.success("Logo removed");
      onChange?.();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex items-center gap-5" data-testid={testId}>
      <div className="h-24 w-24 shrink-0 rounded-md border border-border bg-muted/40 grid place-items-center overflow-hidden" data-testid={`${testId}-preview`}>
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : preview ? (
          <img src={preview} alt="Organization logo" className="h-full w-full object-contain" />
        ) : (
          <ImageIcon className="h-8 w-8 text-muted-foreground" />
        )}
      </div>
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground max-w-sm">
          Upload a square logo (PNG, JPG, GIF or WEBP, up to 5&nbsp;MB). It appears on printed reimbursement vouchers.
        </p>
        <div className="flex items-center gap-2">
          <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid={`${testId}-input`} />
          <Button size="sm" className="rounded-sm" onClick={onPick} disabled={busy} data-testid={`${testId}-upload`}>
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            {preview ? "Replace logo" : "Upload logo"}
          </Button>
          {preview && (
            <Button size="sm" variant="outline" className="rounded-sm text-destructive hover:text-destructive" onClick={remove} disabled={busy} data-testid={`${testId}-remove`}>
              <Trash2 className="h-4 w-4 mr-2" />Remove
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
